import React from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { Add as AddIcon, Check as CheckIcon } from '@mui/icons-material';
import { getBranchAvailability } from '../../utils/stockAvailability';

export interface PharmacyProductItem {
  id: string;
  name: string;
  chainName: string;
  branches: { name: string; quantity: number }[];
  totalQuantity: number;
  isControlled: boolean;
  isRecommended: boolean;
}

interface MedicationCardProps {
  product: PharmacyProductItem;
  isInCart: boolean;
  onAdd: (product: PharmacyProductItem) => void;
  onRequest?: (product: PharmacyProductItem) => void;
}

export const MedicationCard: React.FC<MedicationCardProps> = ({ product, isInCart, onAdd, onRequest }) => {
  return (
    <Box
      sx={{
        background: 'white',
        borderRadius: '14px',
        padding: '14px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${product.isControlled ? '#E53935' : product.isRecommended ? '#F59E0B' : '#E1E8ED'}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {product.isRecommended && (
          <Box sx={{ mb: 0.5 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                background: '#FEF3C7',
                color: '#B45309',
                borderRadius: '4px',
                fontSize: 9,
                fontWeight: 700,
                px: 0.625,
                py: 0.125,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}
            >
              Recomendado
            </Box>
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#14171A', lineHeight: 1.3 }}>
          {product.name}
        </Typography>

        <Typography sx={{ color: '#A0AEC0', fontSize: 10, mt: 0.25 }}>
          {product.chainName}
        </Typography>

        {/* Branch availability chips */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          {product.branches.map((branch) => {
            const { label, bg, color } = getBranchAvailability(branch.name, branch.quantity);

            return (
              <Chip
                key={branch.name}
                label={label}
                size="small"
                sx={{
                  height: 20,
                  background: bg,
                  color,
                  borderRadius: '6px',
                  fontSize: 10,
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Add to cart button */}
      {product.totalQuantity === 0 && onRequest ? (
        <Typography
          onClick={() => onRequest(product)}
          sx={{
            fontSize: 11,
            color: '#0066CC',
            cursor: 'pointer',
            mt: 0.5,
            whiteSpace: 'nowrap',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Solicitar
        </Typography>
      ) : (
        <IconButton
          size="small"
          onClick={() => !isInCart && onAdd(product)}
          disabled={isInCart}
          sx={{
            mt: 0.5,
            width: 32,
            height: 32,
            background: isInCart ? '#E1E8ED' : '#00A86B',
            color: '#fff',
            '&:hover': { background: isInCart ? '#E1E8ED' : '#007F50' },
            '&.Mui-disabled': { color: '#fff', background: '#E1E8ED' },
          }}
        >
          {isInCart ? <CheckIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      )}
    </Box>
  );
};
